import {
    CompassOutlined,
    PieChartFilled,
    ShoppingCartOutlined,
    ShoppingOutlined,
    TeamOutlined,
} from "@ant-design/icons";
import { Badge } from "antd";
import { ReactNode, useCallback, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { UserRole } from "tm-shared/types";

import { ROUTES } from "@/constants";
import {
    useAppSelector,
    useOfflineModeSelector,
    useProtectedUserRoleSelector,
} from "@/hooks/redux";

import { StyledFooter, StyledNavLink } from "./Styled";

interface NavItem {
    to: string;
    label: string;
    icon: ReactNode;
    realActivePath?: string;
    disabled?: boolean;
}

export const Footer = () => {
    const { pathname } = useLocation();
    const role = useProtectedUserRoleSelector();
    const checkoutList = useAppSelector((state) => state.work.checkoutList);
    const activeSchedule = useAppSelector(
        (state) => state.manager.activeSchedule,
    );
    const offlineMode = useOfflineModeSelector();

    const commonLinks: Record<string, NavItem> = useMemo(
        () => ({
            [ROUTES.WorkPlan]: {
                icon: <ShoppingOutlined />,
                to: ROUTES.WorkPlan,
                label: "План Робіт",
            },
            [ROUTES.Dashboard]: {
                icon: <PieChartFilled />,
                to: ROUTES.Dashboard,
                label: "Дашборд",
            },
            [ROUTES.RouteManagement]: {
                icon: <CompassOutlined />,
                to: ROUTES.RouteManagement,
                label: "Маршрути",
            },
            [ROUTES.Team]: {
                icon: <TeamOutlined />,
                to: ROUTES.Team,
                label: "Команда",
            },
            [ROUTES.MyRoute]: {
                icon: <CompassOutlined />,
                to: ROUTES.MyRoute,
                label: "Мій маршрут",
                disabled: offlineMode,
            },
        }),
        [offlineMode],
    );

    const getCommon = useCallback(
        (key: string): NavItem => {
            const item = commonLinks[key];
            if (!item) {
                throw new Error(`NavItem not found for route: ${key}`);
            }
            return item;
        },
        [commonLinks],
    );

    const roleLinks: Record<string, NavItem[]> = useMemo(
        () => ({
            [UserRole.Ta]: [
                getCommon(ROUTES.Dashboard),
                getCommon(ROUTES.MyRoute),
                {
                    icon: (
                        <Badge
                            count={Object.values(checkoutList)?.length || 0}
                            offset={[6, 17]}
                            status="success"
                            color="green"
                        >
                            <ShoppingCartOutlined />
                        </Badge>
                    ),
                    to: ROUTES.Checkout,
                    label: "Кошик",
                },
                getCommon(ROUTES.WorkPlan),
            ],
            [UserRole.Md]: [
                getCommon(ROUTES.Dashboard),
                getCommon(ROUTES.MyRoute),
                getCommon(ROUTES.WorkPlan),
            ],
            [UserRole.Sv]: [
                getCommon(ROUTES.Dashboard),
                getCommon(ROUTES.RouteManagement),
                getCommon(ROUTES.Team),
                {
                    icon: <ShoppingCartOutlined />,
                    to: activeSchedule
                        ? `${ROUTES.SVCheckout}/${activeSchedule.routeId}`
                        : ROUTES.SVCheckout,
                    label: "Кошик",
                    realActivePath: ROUTES.SVCheckout,
                },
                getCommon(ROUTES.WorkPlan),
            ],
            [UserRole.Svm]: [
                getCommon(ROUTES.Dashboard),
                getCommon(ROUTES.RouteManagement),
                getCommon(ROUTES.Team),
                getCommon(ROUTES.WorkPlan),
            ],
            [UserRole.Hos]: [getCommon(ROUTES.Dashboard)],
            [UserRole.Kam]: [
                getCommon(ROUTES.Dashboard),
                {
                    icon: <CompassOutlined />,
                    to: ROUTES.RouteManagementMyRoute,
                    label: "Мій маршрут",
                },
                getCommon(ROUTES.WorkPlan),
            ],
        }),
        [getCommon, checkoutList, activeSchedule],
    );

    const renderNavLinks = roleLinks[role] || [];

    const renderLink = (link: NavItem) => {
        const isActive = link.realActivePath
            ? pathname.includes(link.realActivePath)
            : undefined;

        return (
            <StyledNavLink
                key={link.to}
                to={link.to}
                $isActive={isActive}
                $disabled={link.disabled}
            >
                {link.icon}
                <span>{link.label}</span>
            </StyledNavLink>
        );
    };

    return <StyledFooter>{renderNavLinks.map(renderLink)}</StyledFooter>;
};
